import { signInWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import { auth } from "../firebase";
import { resendVerificationEmail } from "../api/authApi";
import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "./components/ToastHost";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  "http://localhost:3001";

export default function Login() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const nextPath = new URLSearchParams(location.search).get("next") || "/dashboard";
  const [form, setForm] = useState({ email: "", password: "", show: false });

  // Email verification wall state
  const [verifyWall, setVerifyWall]     = useState(false);
  const [firebaseUserRef, setFirebaseUserRef] = useState(null);
  const [resent, setResent]             = useState(false);
  const [checkingVerified, setCheckingVerified] = useState(false);
  const [verifyError, setVerifyError]   = useState("");

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        form.email,
        form.password
      );
      const firebaseUser = userCredential.user;

      // Block unverified email accounts
      if (!firebaseUser.emailVerified) {
        setFirebaseUserRef(firebaseUser);
        setVerifyWall(true);
        return;
      }

      const token = await firebaseUser.getIdToken(true);

      const res = await fetch(`${API_BASE}/api/auth/me`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "Failed to fetch user data");
      }

      const user = data.user ?? data;

      localStorage.setItem(
        "auth",
        JSON.stringify({
          id:    user.id,
          email: user.email,
          role:  user.role,
          name:  user.name,
          token,
        })
      );
      localStorage.setItem("isAuthed", "1");

      navigate(nextPath);
    } catch (err) {
      console.error("Login error:", err);
      toast(t("login.errors.generic"), "error");
    }
  };

  const handleCheckVerified = async () => {
    setCheckingVerified(true);
    setVerifyError("");
    try {
      await firebaseUserRef.reload();
      if (firebaseUserRef.emailVerified) {
        // Now do the real login flow
        const token = await firebaseUserRef.getIdToken(true);
        const res = await fetch(`${API_BASE}/api/auth/me`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || "Failed to fetch user data");
        const user = data.user ?? data;
        localStorage.setItem(
          "auth",
          JSON.stringify({
            id:    user.id,
            email: user.email,
            role:  user.role,
            name:  user.name,
            token,
          })
        );
        localStorage.setItem("isAuthed", "1");
        navigate(nextPath);
      } else {
        setVerifyError(t("auth.verify.notYetError"));
      }
    } catch (err) {
      setVerifyError(err.message);
    } finally {
      setCheckingVerified(false);
    }
  };

  const handleResend = async () => {
    try {
      // Branded email via backend first; Firebase's unbranded default only
      // as a fallback when our SMTP is down.
      try {
        const token = await firebaseUserRef.getIdToken();
        const lang = (i18n.resolvedLanguage || i18n.language || "uk").startsWith("uk") ? "uk" : "en";
        await resendVerificationEmail(token, lang);
      } catch {
        await sendEmailVerification(firebaseUserRef);
      }
      setResent(true);
      setTimeout(() => setResent(false), 5000);
    } catch (_) {}
  };

  // ── Verification wall (shown after login attempt with unverified email) ──
  if (verifyWall) {
    return (
      <main className="min-h-screen bg-[#272c39] text-white">
        <div className="mx-auto max-w-3xl px-4 pt-10 pb-24">
          <div className="h-12" />
          <section className="mx-auto max-w-md text-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#98dea3]/20 text-4xl">
              ✉️
            </div>
            <h1 className="font-unbounded text-2xl font-extrabold tracking-tight">
              {t("auth.verify.title")}
            </h1>
            <p className="mt-3 text-white/70">
              {t("auth.verify.body", { email: form.email })}
            </p>
            <p className="mt-2 text-sm text-white/50">
              {t("auth.verify.spam")}
            </p>

            {verifyError && (
              <p className="mt-4 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-2 text-sm text-red-300">
                {verifyError}
              </p>
            )}

            <button
              onClick={handleCheckVerified}
              disabled={checkingVerified}
              className="mt-8 w-full rounded-xl bg-[#7269e3] px-5 py-3 text-lg font-semibold text-white shadow-[0_6px_20px_rgba(114,105,227,0.35)] hover:brightness-110 disabled:opacity-60"
            >
              {checkingVerified ? t("auth.verify.checking") : t("auth.verify.checkBtn")}
            </button>

            <button
              type="button"
              onClick={handleResend}
              className="mt-4 text-sm text-white/50 hover:text-white/80"
            >
              {resent ? t("auth.verify.resent") : t("auth.verify.resend")}
            </button>

            <button
              type="button"
              onClick={() => setVerifyWall(false)}
              className="mt-3 block w-full text-center text-sm text-white/30 hover:text-white/60"
            >
              {t("auth.verify.backToLogin")}
            </button>
          </section>
        </div>
      </main>
    );
  }

  // ── Normal login form ──
  return (
    <main className="min-h-screen bg-[#272c39] text-white">
      <div className="mx-auto max-w-3xl px-4 pt-10 pb-24">
        <div className="h-12" />

        <section className="mx-auto max-w-md">
          <h1 className="text-center font-unbounded text-3xl font-extrabold tracking-tight">
            {t("login.title")}
          </h1>
          <p className="mt-2 text-center text-white/70">{t("login.subtitle")}</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <label htmlFor="email" className="mb-2 block text-sm text-white/90">
                {t("login.fields.email")}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                placeholder={t("login.placeholders.email")}
                className="w-full rounded-xl border border-white/10 bg-[#7269e3] px-4 py-3 text-white placeholder-white/80 outline-none focus:ring-2 focus:ring-[#a783a6]"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="mb-2 block text-sm text-white/90">
                {t("login.fields.password")}
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={form.show ? "text" : "password"}
                  value={form.password}
                  onChange={onChange}
                  placeholder={t("login.placeholders.password")}
                  className="w-full rounded-xl border border-white/10 bg-[#7269e3] px-4 py-3 pr-12 text-white placeholder-white/80 outline-none focus:ring-2 focus:ring-[#a783a6]"
                  required
                />
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, show: !f.show }))}
                  className="absolute inset-y-0 right-2 my-auto rounded-lg px-2 text-sm text-white/80 hover:text-white"
                  aria-label={form.show ? t("login.actions.hide") : t("login.actions.show")}
                >
                  {form.show ? t("login.actions.hide") : t("login.actions.show")}
                </button>
              </div>
            </div>

            <div className="pt-4">
              <button
                type="submit"
                className="w-full rounded-xl bg-[#7269e3] px-5 py-3 text-lg font-semibold text-white shadow-[0_6px_20px_rgba(114,105,227,0.35)] hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[#a783a6]"
              >
                {t("login.actions.login")}
              </button>
            </div>

            <p className="text-center text-white/80">
              {t("login.switch.prompt")}{" "}
              <Link to="/signup" className="font-medium text-[#98dea3] hover:underline">
                {t("login.switch.link")}
              </Link>
            </p>

            <p className="text-center text-sm text-white/50">
              <Link to="/forgot-password" className="hover:text-white/80 hover:underline">
                {t("login.actions.forgot")}
              </Link>
            </p>
          </form>
        </section>
      </div>
    </main>
  );
}
